// src/screens/StopZones.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Avatar,
  Zoom,
  styled,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import {
  LocationOn,
  Description,
  Edit,
  ArrowForward,
  Add,
  Room,
  Explore,
  Close,
  Search,
  Map,
  DirectionsCar,
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
  },
}));

const LocationChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

const StopZones = () => {
  const [stopzones, setStopZones] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentStopZone, setCurrentStopZone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { control, handleSubmit, reset } = useForm();
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const fetchStopZones = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const response = await axios.get(`${apiUrl}/admins/stopzones`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStopZones(response.data);
    } catch (error) {
      console.error('Error fetching stopzones:', error);
    }
  };

  useEffect(() => {
    fetchStopZones();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = (stopzone = null) => {
    setCurrentStopZone(stopzone);
    if (stopzone) {
      reset(stopzone);
    } else {
      reset({
        name: '',
        description: '',
        address: '',
        latitude: '',
        longitude: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentStopZone(null);
    reset();
  };

  const handleSave = async (data) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (currentStopZone) {
        await axios.put(
          `${apiUrl}/admins/stopzones/${currentStopZone.id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        await axios.post(`${apiUrl}/admins/stopzones`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      fetchStopZones();
      handleClose();
    } catch (error) {
      console.error('Error saving stopzone:', error);
    }
  };

  const openGoogleMaps = (latitude, longitude) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const filteredStopZones = searchTerm
    ? stopzones.filter(
        (zone) =>
          zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          zone.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          zone.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : stopzones;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
          Aree di Sosta
        </Typography>
        
        <Box display="flex" gap={2}>
          <TextField
            placeholder="Cerca area..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Aggiungi Area
          </Button>
        </Box>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Gestisci le aree di sosta camper disponibili e le loro piazzole associate
      </Typography>

      {filteredStopZones.length > 0 ? (
        <Grid container spacing={3}>
          {filteredStopZones.map((stopzone) => (
            <Grid item xs={12} md={6} lg={4} key={stopzone.id}>
              <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                <StyledCard elevation={3}>
                  <CardContent>
                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6" component="div" gutterBottom>
                        {stopzone.name}
                      </Typography>
                      <Avatar 
                        sx={{ 
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main'
                        }}
                      >
                        <DirectionsCar />
                      </Avatar>
                    </Box>

                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2, 
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        minHeight: '40px'
                      }}
                    >
                      {stopzone.description}
                    </Typography>

                    <LocationChip
                      icon={<LocationOn />}
                      label={stopzone.address}
                      sx={{ mb: 2 }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Latitudine
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {stopzone.latitude}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Longitudine
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {stopzone.longitude}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                      startIcon={<Edit />}
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpen(stopzone)}
                      sx={{ mr: 1 }}
                    >
                      Modifica
                    </Button>
                    <Button
                      startIcon={<ArrowForward />}
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/stopzones/${stopzone.id}`)}
                    >
                      Piazzole
                    </Button>
                    <Box flexGrow={1} />
                    <Tooltip title="Vedi su Google Maps">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => openGoogleMaps(stopzone.latitude, stopzone.longitude)}
                      >
                        <Map />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </StyledCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna area di sosta trovata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm ? "Nessun risultato per la ricerca corrente" : "Inizia aggiungendo una nuova area di sosta"}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Aggiungi Area
          </Button>
        </Paper>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {currentStopZone ? 'Modifica Area di Sosta' : 'Aggiungi Area di Sosta'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(handleSave)} sx={{ mt: 2 }}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Il nome è obbligatorio' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nome Area"
                  fullWidth
                  margin="dense"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Room color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{ required: 'La descrizione è obbligatoria' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Descrizione"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={3}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="address"
              control={control}
              rules={{ required: "L'indirizzo è obbligatorio" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Indirizzo"
                  fullWidth
                  margin="dense"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Coordinate
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="latitude"
                  control={control}
                  rules={{ required: 'La latitudine è obbligatoria' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Latitudine"
                      fullWidth
                      margin="dense"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Explore fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="longitude"
                  control={control}
                  rules={{ required: 'La longitudine è obbligatoria' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Longitudine"
                      fullWidth
                      margin="dense"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Explore fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} color="inherit">
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit(handleSave)} 
            variant="contained" 
            color="primary"
          >
            {currentStopZone ? 'Aggiorna' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StopZones;